import React, { useState, useEffect } from 'react'
import { X, AlertTriangle, Check } from 'lucide-react'
import { getFunctions, httpsCallable } from 'firebase/functions'

interface ConflictField {
  field: string
  existingValue: any
  googleValue: any
}

interface SyncConflict {
  memberId: string
  memberName: string
  memberEmail: string
  conflicts: ConflictField[]
}

interface SyncConflictModalProps {
  isOpen: boolean
  onClose: () => void
  onResolve: () => void
  conflicts: SyncConflict[]
}

type Resolution = 'keep' | 'google'

export const SyncConflictModal: React.FC<SyncConflictModalProps> = ({
  isOpen,
  onClose,
  onResolve,
  conflicts
}) => {
  // Track resolution choice for each conflict field
  const [resolutions, setResolutions] = useState<Map<string, Map<string, Resolution>>>(
    () => {
      const map = new Map()
      // Guard against undefined or empty conflicts
      if (conflicts && Array.isArray(conflicts)) {
        conflicts.forEach(c => {
          const fieldMap = new Map()
          if (c.conflicts && Array.isArray(c.conflicts)) {
            c.conflicts.forEach(f => {
              fieldMap.set(f.field, 'keep') // Default to keeping existing
            })
          }
          map.set(c.memberId, fieldMap)
        })
      }
      return map
    }
  )
  const [resolving, setResolving] = useState(false)
  const [error, setError] = useState('')

  // Update resolutions when conflicts change
  useEffect(() => {
    if (conflicts && Array.isArray(conflicts) && conflicts.length > 0) {
      const map = new Map()
      conflicts.forEach(c => {
        const fieldMap = new Map()
        if (c.conflicts && Array.isArray(c.conflicts)) {
          c.conflicts.forEach(f => {
            fieldMap.set(f.field, 'keep')
          })
        }
        map.set(c.memberId, fieldMap)
      })
      setResolutions(map)
    }
  }, [conflicts])

  const functions = getFunctions()

  const handleResolutionChange = (memberId: string, field: string, resolution: Resolution) => {
    setResolutions(prev => {
      const next = new Map(prev)
      const fieldMap = new Map(next.get(memberId))
      fieldMap.set(field, resolution)
      next.set(memberId, fieldMap)
      return next
    })
  }

  const handleResolveAll = async () => {
    try {
      setResolving(true)
      setError('')

      // Filter valid conflicts first
      const validConflictsForResolve = (conflicts || []).filter(
        c => c && c.conflicts && Array.isArray(c.conflicts) && c.conflicts.length > 0
      )

      // Build resolution updates for each member
      const resolutionUpdates = validConflictsForResolve.map(conflict => {
        const fieldResolutions = resolutions.get(conflict.memberId)
        const updates: Record<string, any> = {}
        const keepExisting: Record<string, any> = {} // Fields where user chose to keep Firebase value

        if (fieldResolutions && conflict.conflicts) {
          conflict.conflicts.forEach(field => {
            const choice = fieldResolutions.get(field.field)
            if (choice === 'google') {
              // User chose Google's value - update Firebase
              updates[field.field] = field.googleValue
            } else if (choice === 'keep') {
              // User chose to keep Firebase's value - push to Google
              keepExisting[field.field] = field.existingValue
            }
          })
        }

        return {
          memberId: conflict.memberId,
          memberEmail: conflict.memberEmail,
          updates,
          keepExisting
        }
      }).filter(r => Object.keys(r.updates).length > 0 || Object.keys(r.keepExisting).length > 0)

      if (resolutionUpdates.length > 0) {
        const resolveFunc = httpsCallable(functions, 'resolveSyncConflicts')
        await resolveFunc({ resolutions: resolutionUpdates })
      }

      onResolve()
    } catch (err: any) {
      console.error('Error resolving conflicts:', err)
      setError(err.message || 'Failed to resolve conflicts')
    } finally {
      setResolving(false)
    }
  }

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return '(empty)'
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
  }

  const formatFieldName = (field: string): string => {
    const names: Record<string, string> = {
      primaryEmail: 'Email',
      name: 'Name',
      thumbnailPhotoUrl: 'Photo URL',
      phone: 'Phone',
      jobTitle: 'Job Title',
      orgUnitPath: 'Org Unit',
      address: 'Address'
    }
    return names[field] || field
  }

  // Filter to only valid conflicts with actual conflict fields
  const validConflicts = (conflicts || []).filter(
    c => c && c.conflicts && Array.isArray(c.conflicts) && c.conflicts.length > 0
  )

  if (!isOpen || validConflicts.length === 0) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Sync Conflicts Detected
              </h2>
              <p className="text-sm text-gray-500">
                {validConflicts.length} member{validConflicts.length !== 1 ? 's have' : ' has'} conflicting data
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Conflicts list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {validConflicts.map(conflict => (
            <div key={conflict.memberId} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Member header */}
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <h3 className="font-medium text-gray-900">{conflict.memberName || 'Unknown'}</h3>
                <p className="text-sm text-gray-500">{conflict.memberEmail || ''}</p>
              </div>

              {/* Conflict fields */}
              <div className="divide-y divide-gray-100">
                {(conflict.conflicts || []).map(field => {
                  const currentResolution = resolutions.get(conflict.memberId)?.get(field.field) || 'keep'
                  
                  return (
                    <div key={field.field} className="p-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        {formatFieldName(field.field)}
                      </p>
                      
                      <div className="grid grid-cols-2 gap-4">
                        {/* Keep existing */}
                        <button
                          onClick={() => handleResolutionChange(conflict.memberId, field.field, 'keep')}
                          className={`p-3 rounded-lg border-2 text-left transition-colors ${
                            currentResolution === 'keep'
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-gray-500">Keep Existing</span>
                            {currentResolution === 'keep' && (
                              <Check className="w-4 h-4 text-purple-600" />
                            )}
                          </div>
                          <p className="text-sm text-gray-900 break-words">
                            {formatValue(field.existingValue)}
                          </p>
                        </button>

                        {/* Use Google */}
                        <button
                          onClick={() => handleResolutionChange(conflict.memberId, field.field, 'google')}
                          className={`p-3 rounded-lg border-2 text-left transition-colors ${
                            currentResolution === 'google'
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-gray-500">Use Google</span>
                            {currentResolution === 'google' && (
                              <Check className="w-4 h-4 text-blue-600" />
                            )}
                          </div>
                          <p className="text-sm text-gray-900 break-words">
                            {formatValue(field.googleValue)}
                          </p>
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleResolveAll}
            disabled={resolving}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
          >
            {resolving ? 'Resolving...' : 'Apply Resolutions'}
          </button>
        </div>
      </div>
    </div>
  )
}
